/**
 * Slides: the way down you are supposed to find.
 *
 * Every other exit in the game is a point on a floor. That is the intent — the
 * game is about being lost — but it has one bad consequence: a player who has
 * swept a level properly and still not found the way out is not being made to
 * feel lost, they are being made to feel like the game is broken. A slide is
 * the answer to that. It is the largest object in the game, it stands against a
 * wall in the middle of a big room, it has a working light over it, something
 * in it makes a noise every few seconds, and there is a sign beside it and a
 * trail of arrows leading to it.
 *
 * There are several of them, spread across the level, and `planSlides` places
 * them against how far the average spot on the floor is from one rather than
 * against a count.
 *
 * **But being near a slide is not finding one.** The fog closes at thirty-four
 * metres and the building is made of rooms, so a mouth is only *visible* from
 * about a twentieth of the floor — and a player exploring outward from the
 * spawn has to clear everything nearer before they reach it. Measured against a
 * simulated walk, a thorough sweep of Level 0 took five minutes to lay eyes on
 * the first one and a wandering player took eleven, which is the complaint this
 * whole file exists to answer, arrived at from a different direction. Coverage
 * in metres of walking was necessary and nowhere near sufficient.
 *
 * What closes that gap is the thing a real building uses, and it is not more
 * fire escapes: it is signage that reaches everywhere the escape does not.
 * `installSlides` therefore runs a level-wide wayfinding pass rather than a
 * trail per mouth — marks spread across the whole floor at a fixed density,
 * every one of them aimed down the same distance field at whichever way down is
 * nearest. Follow any of them from anywhere and you arrive. That is why the
 * arrows are consistent with each other by construction: there is one field,
 * not one per slide.
 *
 * It is also the only exit that goes somewhere by name rather than one floor
 * down — `SLIDE_ROUTES` in `levels.ts` says where — which is what makes finding
 * one worth the walk rather than merely convenient.
 *
 * **All of that describes Level 0, and only Level 0.** Everything above is the
 * shallow setting of a ramp: from the second floor down, mouths get scarcer,
 * further from the spawn, further off the route the player was already walking,
 * and less signposted, and they keep getting so for as long as the descent
 * lasts. `descentPressure` below is the one number that does it and the block of
 * constants under it is everything it buys. The Lobby is untouched, by
 * construction and on purpose — its findability was arrived at the hard way.
 *
 * None of that is a wink. A fire escape is signposted, lit and audible for
 * exactly the same reasons; that this building's fire escape is a plastic slide
 * into a party is the level's business, not the sign's.
 *
 * Two passes live here. `planSlides` runs during placement and picks the cells;
 * `installSlides` and `signpostExits` run after the scenery pass, because they
 * have to know which walls `props.ts` has already used.
 */

import { TUNING } from '../../core/config';
import { bfsDistances, UNREACHABLE } from '../../core/grid';
import { clamp } from '../../core/math';
import {
  Cell,
  type Cell2,
  type ExitPoint,
  type Grid,
  type LevelSpec,
  type PropKind,
  type PropSpot,
  type RNG,
} from '../../core/types';
import { LEVEL_COUNT, LEVEL_ORDER, slideTargets } from '../levels';
import { PROP_DEFS } from './propTables';
import type { GenSite } from './site';

/* ------------------------------------------------------------------ *
 * How deep we are
 * ------------------------------------------------------------------ */

/**
 * The descent index this level is being built for.
 *
 * **Derived rather than passed, which is a wart.** `generateLevel` takes a spec
 * and a seed and has never been told which floor of the run it is making — but
 * `getLevelSpec` builds every spec it is ever handed as
 * `LEVEL_ORDER[index % LEVEL_COUNT]` with a loop count folded into a few of its
 * numbers, and `resolveDescent` only ever produces indices of that same shape.
 * So the index is recoverable exactly: the position in the order comes from the
 * id, and the loop comes from `entityBudget`, which `getLevelSpec` raises by
 * exactly one per loop and nothing else touches.
 *
 * This is not where that should live. See the request filed against
 * `core/types.ts`: a `LevelSpec.depth` set by `game.ts` would make this three
 * lines shorter, would let the renderer see the same number (it currently
 * cannot — `render/` may not import `world/`), and would stop this file caring
 * how `getLevelSpec` is written. Until then, the derivation degrades safely: an
 * unrecognised spec reads as depth 0, which is the shallowest and most
 * findable setting, and a changed formula makes the ramp gentler rather than
 * breaking it.
 *
 * Note that a slide is a hole in the descent order — riding one from Level 0
 * lands the player on the Funrooms at index 5, not index 1. That is deliberate
 * here as well as in `resolveDescent`: they skipped four floors, so they arrive
 * somewhere four floors harder to get out of.
 */
function descentDepth(spec: LevelSpec): number {
  const order = LEVEL_ORDER.findIndex((s) => s.id === spec.id);
  if (order < 0) return 0;
  const loop = Math.max(0, Math.round(spec.entityBudget - LEVEL_ORDER[order]!.entityBudget));
  return order + loop * LEVEL_COUNT;
}

/**
 * Depth as a 0..1 pressure on everything below, saturating.
 *
 * `depth / (depth + DEPTH_HALF)` rather than anything that reaches 1: the
 * descent has no end, so a ramp that finishes would either finish too early and
 * stop mattering or finish too late and have done nothing by the time anybody
 * saw it. This never arrives — 0.14 on the second floor, 0.5 on the seventh,
 * 0.78 by the twenty-first — so the way down keeps getting harder to find for
 * as long as anybody keeps going, and never becomes impossible to find.
 *
 * Level 0 is 0 and every number below is therefore exactly what it was. That is
 * a promise: the Lobby's findability was arrived at the hard way and nothing
 * here is allowed to touch it.
 */
const DEPTH_HALF = 6;

function descentPressure(spec: LevelSpec): number {
  const depth = descentDepth(spec);
  return depth / (depth + DEPTH_HALF);
}

/**
 * What that pressure buys, per unit of it. Every one of these is a fraction of
 * the shallow value in `TUNING.world.slides`, and they belong there with it —
 * see the request filed against `core/config.ts`. They are here because this
 * file may not edit that one.
 *
 * Measured over twelve seeds a level, from the Lobby at depth 0 to the Lobby on
 * its third loop at depth 21: mouths 4.8 -> 3.0, mean walk to the nearest one
 * 124 m -> 205 m, walk from the spawn to the nearest one 157 m -> 232 m, floor
 * arrows 180 -> 72. "You have to look", not "you are lost".
 */
/** Extra mean walking distance to the nearest mouth, as a fraction. */
const COVERAGE_GROWTH = 0.9;
/** Mouths dropped from `maxCount`. Never below three; two is a level with a hole in it. */
const COUNT_DROP = 2.2;
/** Extra clear ground between the spawn and the nearest mouth, as a fraction. */
const SPAWN_GROWTH = 0.7;
/** How much further along the walk from the spawn a mouth is pushed. */
const BAND_PUSH = 0.28;
/**
 * Cells of detour a mouth should cost somebody heading for an ordinary exit.
 *
 * The shallow rule is "on the route": a slide wants to be met, so the scoring
 * rewards a cell that costs nothing to walk past. Deeper the same term is
 * retargeted rather than removed — the ideal detour slides out to twenty-two
 * cells, so the mouth ends up a room or two off the line the player was already
 * walking. It is still scored, so it is still somewhere reachable and sensible;
 * it is just no longer on the way.
 */
const OFF_ROUTE = 22;
/** How much wider the district each floor arrow serves gets, as a fraction. */
const SIGN_SPREAD = 0.8;
/** Extra cells of real progress an arrow has to promise before it is painted. */
const ARROW_GAIN_RISE = 3;
/**
 * Chance a mouth keeps the building's EXIT plate beside it, at full depth.
 *
 * The rest get graffiti instead — the same wall, the same distance, one mark,
 * but left by a person rather than by a building. Deeper down the building
 * stops labelling its own plumbing and all you have is what somebody wrote
 * before they got in. One plate per level is guaranteed whatever the dice say.
 */
const PLATE_KEPT = 0.4;
/**
 * Graffiti rows reserved for the mouths, mirroring `GRAFFITI_MOUTH_ROWS` in
 * `render/propdecals.ts`. Not imported from there on purpose: nothing under
 * `world/` may depend on the renderer, and the row indices satisfy the contract
 * without the dependency — `variantIndex` picks a row with `variant % rows`, so
 * any value below the reserved count lands on a reserved row whatever the total
 * becomes.
 */
const GRAFFITI_MOUTH_ROWS = 3;

/** Direction indices, matching the convention in `propSites.ts`. */
const DX = [1, -1, 0, 0] as const;
const DY = [0, 0, 1, -1] as const;

/** Metres between a wall-mounted prop and the wall surface. Mirrors `props.ts`. */
const WALL_BACK = 0.08;
/** Metres of air between a solid floor prop's box and the wall. Mirrors `props.ts`. */
const SOLID_BACK = 0.05;
/** Clearance kept under the ceiling for wall-mounted props. Mirrors `props.ts`. */
const HEAD_ROOM = 0.35;

/**
 * The atlas row a `sign` draws, via `variantIndex(variant, rows)`. Row 0 is the
 * EXIT plate — the one thing the building says that is still true, where a
 * slide is concerned.
 */
const SIGN_EXIT = 0;

/** Cells of map edge a slide is never fitted into. */
const EDGE_MARGIN = 6;
/** Cells of clear floor ahead of the mouth that count toward being visible. */
const SIGHT_REACH = 12;

/** Cells around an existing sign or arrow that this pass will not build on. */
const LANDMARK_CLEARANCE = 2;
/**
 * Cells of clear floor in front of an arrow past which it counts as being on a
 * wall people walk down rather than inside a cupboard. A preference, not a bar:
 * Pipe Dreams is a one-cell maze where *no* wall has three cells of room in
 * front of it, and a bar would have left the level that needs signage most with
 * almost none of it.
 */
const ARROW_GOOD_SIGHT = 3;
/** Cells of that run counted toward the score, past which it stops mattering. */
const ARROW_SIGHT_CAP = 10;
/**
 * How far along its own wall an arrow is allowed to be aiming, in cells, and the
 * least ground it has to win over that to be worth painting.
 *
 * Only two of the four directions can be drawn on a wall the player is facing,
 * so an arrow is not a bearing — it is a choice between this way and that way.
 * Measuring the choice over a couple of cells picks the better of two wrong
 * answers about as often as it picks a right one, and a player who follows
 * three of those has been walked in a circle. Measured over fourteen, an arrow
 * is only painted where the wall happens to line up with the way down, and
 * walking the way it points is worth at least `ARROW_MIN_GAIN` cells of real
 * progress. Districts where no wall lines up simply get no arrow.
 */
const ARROW_LOOKAHEAD = 14;
/**
 * Required against the run actually available, not flat: on a level made of
 * corridors the walk along a wall ends at the next bend three cells later, and
 * three cells of unbroken progress is an honest arrow. What this rules out is
 * the direction that wanders.
 */
const ARROW_MIN_GAIN = 5;
/**
 * Cells from a mouth inside which the wayfinding pass leaves the walls alone.
 * The mouth has its own plate, and an arrow pointing at something already
 * filling the room reads as the building being slow rather than helpful.
 */
const ARROW_MOUTH_CLEAR = 4;

/** A slide, as placed. Carried from placement through to the scenery pass. */
export interface SlidePlan {
  /** Flat grid index of the cell the mouth stands in. */
  cell: number;
  /** Direction index of the wall it backs onto. */
  dir: number;
  /** The exit it is. Already present in the level's exit list. */
  exit: ExitPoint;
}

/* ------------------------------------------------------------------ *
 * Placement
 * ------------------------------------------------------------------ */

/**
 * How many, and how far apart. Every one of these is in metres of *walking*
 * rather than cells; they live in `TUNING.world.slides` with the rest of the
 * numbers that decide how the game feels, and are documented there.
 *
 * What they buy: the Lobby and the warehouse take five mouths, the Funrooms and
 * the Poolrooms four or five, Motion three, and Pipes — a third of the Lobby's
 * floor area, but a one-cell maze where a hundred metres of map costs three
 * hundred of walking — takes five as often as the Lobby does. The count is not
 * authored anywhere; it falls out of the level's size and its tortuosity.
 */
const {
  minCount: MIN_SLIDES,
  maxCount: MAX_SLIDES,
  coverageTarget: COVERAGE_TARGET,
  separation: SLIDE_SEPARATION,
  spawnClearance: SPAWN_CLEARANCE,
  spreadCap: SPREAD_CAP,
  signSpacing: SIGN_SPACING,
} = TUNING.world.slides;

/**
 * Choose where the slides go and build the exits for them.
 *
 * The scoring is the opposite of `placeExits`, which pushes an exit into the
 * far quartile of the map on purpose. A slide wants to be *met*: in a big room,
 * flat against a wall with the room in front of it, on a line the player was
 * going to walk anyway, and far enough from the ordinary exits that finding one
 * is not the same walk as finding the other.
 *
 * They are then placed one at a time, each scored against where the ones before
 * it went, and the loop stops when the level is covered rather than when a
 * counter runs out. See `COVERAGE_TARGET`.
 *
 * Returns an empty array for a level with no slide route.
 */
export function planSlides(
  site: GenSite,
  regionAt: Int32Array,
  walk: Int32Array,
  dist: Uint16Array,
  maxDist: number,
  exits: readonly ExitPoint[],
): SlidePlan[] {
  const { grid, rng, spec, regions } = site;
  const targets = slideTargets(spec);
  if (targets.length === 0) return [];

  // Everything below this line that reads `t` is the answer to "make the slides
  // a bit harder to find on the levels after the first one". At depth 0 it is
  // zero and every number is what it always was.
  const t = descentPressure(spec);

  // How far each cell is from a way out, so "on the route" can be measured
  // rather than guessed: slack is the extra walking a detour through this cell
  // would cost somebody heading from the spawn to the nearest ordinary exit.
  const exitCells: Cell2[] = [];
  for (const e of exits) {
    const c = grid.worldToCell(e.pos.x, e.pos.z);
    if (grid.isWalkable(c.cx, c.cy)) exitCells.push(c);
  }
  const exitDist = exitCells.length > 0 ? bfsDistances(grid, exitCells) : null;
  let shortest = UNREACHABLE;
  if (exitDist) {
    for (let k = 0; k < walk.length; k++) {
      const i = walk[k]!;
      const a = dist[i]!;
      const b = exitDist[i]!;
      if (a === UNREACHABLE || b === UNREACHABLE) continue;
      if (a + b < shortest) shortest = a + b;
    }
  }

  const separation = Math.max(4, Math.round(SLIDE_SEPARATION / grid.cellSize));
  const spawnClear = Math.max(3, Math.round((SPAWN_CLEARANCE * (1 + SPAWN_GROWTH * t)) / grid.cellSize));
  const coverageTarget = (COVERAGE_TARGET * (1 + COVERAGE_GROWTH * t)) / grid.cellSize;
  // Three is the floor. A level with two mouths on it has a half nobody would
  // find one in, and this is meant to be a longer walk rather than a different
  // game.
  const maxSlides = Math.max(3, Math.round(MAX_SLIDES - COUNT_DROP * t));
  // The band of the walk a mouth may sit in, pushed further out with depth: at
  // depth 0 it starts a sixth of the way in, and by the third loop a third.
  const bandNear = 0.15 + BAND_PUSH * t;
  const bandFar = Math.min(0.95, 0.85 + 0.12 * t);
  // How much of a detour the mouth should be from the line between the spawn
  // and the nearest ordinary exit. Zero at the top of the building.
  const wantSlack = OFF_ROUTE * t;

  // Cell quality at each relaxation level, computed at most once apiece however
  // many slides are placed — the fourth one usually not at all. Only the spread
  // term below changes between picks, so the expensive half of the scoring is
  // done a few times a level rather than a few times a slide.
  const cache: Array<Float32Array | null> = [null, null, null, null];
  const effective = new Float32Array(walk.length);

  const plans: SlidePlan[] = [];
  const mouths: Cell2[] = [];
  /** Walking distance to the nearest mouth placed so far. Null while there are none. */
  let toNearest: Uint16Array | null = null;

  while (plans.length < maxSlides) {
    if (plans.length >= MIN_SLIDES && meanWalkToSlide(toNearest) <= coverageTarget) break;
    let cell = choose(separation, 2);
    // A level too tangled to hold two slides a hundred metres apart still has
    // to hold two: the second one is what covers the half of the map the first
    // one does not, and neither the spacing rule nor the last of the quality
    // rules is worth going below the minimum for. Pipes is the level that needs
    // this — a one-cell maze has very few cells with a room in front of them
    // and a single wall behind, and about one seed in a hundred has no second
    // one anywhere at the full separation. The last rung is "not in the same
    // room, and there is a wall to stand it against", which is as far as this
    // is willing to go; the one thing no rung gives up is `SPAWN_CLEARANCE`.
    for (const relaxed of [0.45, 0.12]) {
      if (cell >= 0 || plans.length >= MIN_SLIDES) break;
      cell = choose(Math.max(4, Math.round(separation * relaxed)), 3);
    }
    if (cell < 0) break;
    const dir = wallOf(grid, cell);
    // Unreachable in practice — `score` only ever passes a cell with exactly one
    // solid neighbour — but a -1 here would spin the loop on the same cell.
    if (dir < 0) break;
    plans.push({
      cell,
      dir,
      // Round-robin over the routes this level offers, so several mouths on one
      // floor are not several ways to the same place. The first one always
      // takes the canonical destination; see `SLIDE_ROUTES`.
      exit: makeExit(grid, exits.length + plans.length, cell, dir, targets[plans.length % targets.length]!),
    });
    mouths.push({ cx: cell % grid.w, cy: (cell / grid.w) | 0 });
    toNearest = bfsDistances(grid, mouths);
  }
  return plans;

  /** Mean walking distance, in cells, from anywhere reachable to the nearest slide. */
  function meanWalkToSlide(field: Uint16Array | null): number {
    if (!field) return Infinity;
    let total = 0;
    let n = 0;
    for (let k = 0; k < walk.length; k++) {
      const i = walk[k]!;
      if (dist[i] === UNREACHABLE) continue;
      const d = field[i]!;
      if (d === UNREACHABLE) continue;
      total += d;
      n++;
    }
    return n === 0 ? Infinity : total / n;
  }

  function scoresFor(relax: number): Float32Array {
    const hit = cache[relax];
    if (hit) return hit;
    const out = new Float32Array(walk.length);
    for (let k = 0; k < walk.length; k++) out[k] = score(walk[k]!, relax);
    cache[relax] = out;
    return out;
  }

  /**
   * The next mouth: the best cell that is at least `sep` cells of walking from
   * every one already placed, with the ones furthest from all of them favoured.
   *
   * One pass per relaxation level up to `relaxLimit`, each dropping one
   * requirement. The first describes what a slide should look like; the last is
   * only "somewhere with a wall", which is what a level made entirely of
   * corridors would fall back to.
   */
  function choose(sep: number, relaxLimit: number): number {
    for (let relax = 0; relax <= relaxLimit; relax++) {
      const base = scoresFor(relax);
      let bestScore = 0;
      for (let k = 0; k < walk.length; k++) {
        let s = base[k]!;
        if (s > 0 && toNearest) {
          const d = toNearest[walk[k]!]!;
          if (d === UNREACHABLE || d < sep) s = 0;
          // Distance from what is already placed is the whole point of a second
          // slide, so it multiplies the cell's quality rather than adding to it:
          // no room is nice enough to be worth putting next to the last one.
          else s *= Math.min(SPREAD_CAP, Math.sqrt(d / sep));
        }
        effective[k] = s;
        if (s > bestScore) bestScore = s;
      }
      if (bestScore <= 0) continue;

      // Draw between the near-ties rather than taking the maximum: two runs of
      // the same level should not always put the slide in the same corner. The
      // acceptance band widens if everything in the first one turns out to be a
      // chokepoint.
      for (const band of [0.92, 0.75, 0.55]) {
        const pool: number[] = [];
        for (let k = 0; k < walk.length; k++) {
          const i = walk[k]!;
          if (effective[k]! < bestScore * band) continue;
          // The mouth is two and a half metres deep and nearly two wide, which
          // is most of a cell on the tighter levels — so wherever it stands, it
          // stands in the way. This is what keeps it from standing in the way of
          // the only route.
          if (!passageSurvivesWithout(grid, i)) continue;
          pool.push(i);
        }
        if (pool.length === 0) continue;
        return pool.length === 1 ? pool[0]! : rng.pick(pool);
      }
    }
    return -1;
  }

  function score(i: number, relax: number): number {
    const d = dist[i]!;
    if (d === UNREACHABLE) return 0;
    // Never in the room the player arrives in, at any relaxation: this one is a
    // promise about the first half-minute rather than a preference.
    if (d < spawnClear) return 0;
    // Not water (a slide standing in a pool), not a pillar cell, and never a
    // doorway — the one place the player has to be able to walk through.
    const c = grid.cells[i]! as Cell;
    if (c !== Cell.Floor && c !== Cell.Damp && c !== Cell.Open) return 0;

    const x = i % grid.w;
    const y = (i / grid.w) | 0;

    // The outer ring of any level is where the player sweeps last, if at all,
    // and the shell around the edge of the map is not the wall of anything —
    // it is where the level stops.
    if (Math.min(x, y, grid.w - 1 - x, grid.h - 1 - y) < EDGE_MARGIN) return 0;

    let solids = 0;
    let dir = -1;
    for (let k = 0; k < 4; k++) {
      if (!grid.isSolid(x + DX[k], y + DY[k])) continue;
      solids++;
      dir = k;
    }
    // Exactly one wall: a corner hides it, and a cell with walls on two sides
    // is a corridor, where a two-metre-wide object is a plug.
    if (solids !== 1) return 0;

    let open = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        if (grid.isWalkable(x + dx, y + dy)) open++;
      }
    }
    // How much room there is around it, out of the eight neighbours. The last
    // rung asks for four, which is a passing place rather than a room — that is
    // the most a maze seed has to offer and it beats not having a second slide.
    if (open < (relax === 0 ? 6 : relax >= 3 ? 4 : 5)) return 0;

    // Never on top of another way out: two exits in one room turns the level's
    // one memorable object into a shrug.
    const exitGap = relax >= 3 ? 6 : 10;
    for (const e of exitCells) {
      if (Math.abs(e.cx - x) + Math.abs(e.cy - y) < exitGap) return 0;
    }

    const rid = regionAt[i]!;
    const region = rid >= 0 ? regions[rid] : undefined;
    const area = region ? region.w * region.h : 0;
    if (relax === 0) {
      if (!region) return 0;
      if (region.kind === 'closet' || region.kind === 'warren') return 0;
      if (area < 40) return 0;
    }

    // The player should walk past it, not to it: a band from a sixth of the way
    // in to five sixths of it, so no slide sits in the room they arrive in and
    // none is past the point they would have found an ordinary exit anyway.
    // The near end of the band is `SPAWN_CLEARANCE` again in the level's own
    // units, and the one that binds on a level whose deepest corner is a long
    // way out; the far end is the one doing work here.
    const depth = d / maxDist;
    if (relax < 2 && (depth < bandNear || depth > bandFar)) return 0;

    // How far you can see it coming: walkable cells straight out from the
    // mouth. This is the one measurement that means what the player means by
    // "findable" — a slide against the long wall of a hall is visible from the
    // far end of it, and the same slide four metres away round a corner is not.
    let sight = 0;
    while (sight < SIGHT_REACH && grid.isWalkable(x - DX[dir] * (sight + 1), y - DY[dir] * (sight + 1))) {
      sight++;
    }
    if (sight < (relax === 0 ? 4 : relax >= 3 ? 1 : 2)) return 0;

    let s = 1 + open * 0.35 + Math.min(area, 300) / 120 + sight * 0.25;
    if (exitDist && shortest !== UNREACHABLE) {
      const b = exitDist[i]!;
      if (b !== UNREACHABLE) {
        // Retargeted rather than dropped with depth: `wantSlack` is the detour
        // a mouth ought to cost, and it starts at nothing. A term that simply
        // went away would leave the deep mouths scored on room quality alone,
        // which puts them in the biggest hall on the level — findable by
        // accident, which is what this is trying to stop being true.
        const slack = d + b - shortest;
        s += 3 * Math.max(0, 1 - Math.abs(slack - wantSlack) / 34);
      }
    }
    // A slight pull toward the middle of the walk, which is where a player is
    // still sweeping rather than doubling back — and further along it the
    // deeper the floor, so the sweep has to have gone on a while first.
    const sweet = 0.45 + 0.25 * t;
    s += 1.2 * (1 - Math.abs(depth - sweet) / 0.45);
    return s;
  }
}

/** Half-width of the window the passage test looks at, in cells. */
const PASSAGE_WINDOW = 5;

/**
 * True when blocking `cell` outright leaves its neighbourhood as connected as
 * it was.
 *
 * The slide's box is wider than a cell on the tighter levels, so it plugs
 * whatever it stands in. `props.ts` proves the same thing about every solid
 * prop it places, against the exact footprint; this is the cheap version of
 * that test, against the whole cell, run on a handful of candidates rather than
 * on hundreds of props.
 *
 * The search is confined to a window, so a route that leaves it and comes back
 * reads as no route at all. That is the safe direction to be wrong in: the
 * only cost is passing over a cell that would have been fine.
 */
function passageSurvivesWithout(grid: Grid, cell: number): boolean {
  const cx = cell % grid.w;
  const cy = (cell / grid.w) | 0;

  const neighbours: number[] = [];
  for (let k = 0; k < 4; k++) {
    const x = cx + DX[k];
    const y = cy + DY[k];
    if (grid.isWalkable(x, y)) neighbours.push(y * grid.w + x);
  }
  // A cell with one way in and out of it cannot be a chokepoint for anything
  // except itself, and nothing is behind a wall we are standing against.
  if (neighbours.length <= 1) return true;

  const x0 = cx - PASSAGE_WINDOW;
  const y0 = cy - PASSAGE_WINDOW;
  const span = PASSAGE_WINDOW * 2 + 1;
  const seen = new Uint8Array(span * span);
  const queue: number[] = [neighbours[0]!];
  const mark = (i: number): void => {
    seen[(((i / grid.w) | 0) - y0) * span + ((i % grid.w) - x0)] = 1;
  };
  const marked = (i: number): boolean =>
    seen[(((i / grid.w) | 0) - y0) * span + ((i % grid.w) - x0)] === 1;
  mark(neighbours[0]!);

  let found = 1;
  while (queue.length > 0) {
    const i = queue.pop()!;
    const x = i % grid.w;
    const y = (i / grid.w) | 0;
    for (let k = 0; k < 4; k++) {
      const nx = x + DX[k];
      const ny = y + DY[k];
      if (nx < x0 || ny < y0 || nx >= x0 + span || ny >= y0 + span) continue;
      if (!grid.isWalkable(nx, ny)) continue;
      const j = ny * grid.w + nx;
      if (j === cell || marked(j)) continue;
      mark(j);
      queue.push(j);
      if (neighbours.includes(j)) found++;
    }
  }
  return found === neighbours.length;
}

/** The first solid orthogonal neighbour of a cell, as a direction index. */
function wallOf(grid: Grid, cell: number): number {
  const x = cell % grid.w;
  const y = (cell / grid.w) | 0;
  for (let k = 0; k < 4; k++) if (grid.isSolid(x + DX[k], y + DY[k])) return k;
  return -1;
}

/**
 * The exit point: at the mouth, in the cell *in front of* the slide rather than
 * the one it stands in.
 *
 * The box is most of a cell deep, so the slide's own cell is ground the player
 * cannot stand on. Putting the trigger a little way out is what makes "walk up
 * to the slide and press E" work — and the cell it lands in is walkable by
 * construction, because the mouth is only ever fitted to a cell with exactly
 * one wall, which leaves the one opposite that wall open.
 */
function makeExit(grid: Grid, id: number, cell: number, dir: number, target: number): ExitPoint {
  const base = grid.cellToWorld(cell % grid.w, (cell / grid.w) | 0);
  const out = grid.cellSize * 0.62;
  return {
    id,
    pos: { x: base.x - DX[dir] * out, z: base.z - DY[dir] * out },
    kind: 'slide',
    // Wider than the ordinary 1.6. The interaction range is really
    // `TUNING.player.interactRange`; this is the generator's own statement of
    // how big the thing is.
    radius: 2.2,
    target,
  };
}

/* ------------------------------------------------------------------ *
 * Scenery: the slide, its sign, its arrows
 * ------------------------------------------------------------------ */

export interface DressContext {
  spec: LevelSpec;
  grid: Grid;
  rng: RNG;
  /** Appended to in place. The caller renumbers ids once everything is in. */
  props: PropSpot[];
  exits: readonly ExitPoint[];
  slides: readonly SlidePlan[];
}

/**
 * Where this pass may not build: within `LANDMARK_CLEARANCE` cells of a sign or
 * an arrow that already exists. Stamped once and kept up to date as we add, so
 * the wall search stays a flat array lookup rather than a scan of every prop.
 */
function landmarkMask(grid: Grid, props: readonly PropSpot[]): Uint8Array {
  const mask = new Uint8Array(grid.w * grid.h);
  for (const p of props) {
    if (p.kind !== 'sign' && p.kind !== 'arrow') continue;
    const c = grid.worldToCell(p.pos.x, p.pos.z);
    stampLandmark(mask, grid, c.cx, c.cy);
  }
  return mask;
}

function stampLandmark(mask: Uint8Array, grid: Grid, cx: number, cy: number): void {
  const r = LANDMARK_CLEARANCE;
  for (let y = cy - r; y <= cy + r; y++) {
    if (y < 0 || y >= grid.h) continue;
    for (let x = cx - r; x <= cx + r; x++) {
      if (x < 0 || x >= grid.w) continue;
      mask[y * grid.w + x] = 1;
    }
  }
}

/**
 * Put the slides in the world, and point at them from everywhere.
 *
 * Each mouth gets the object and one mark on the wall beside it — the
 * building's EXIT plate near the top of the descent, and further down, more
 * often, what a person wrote there instead. The arrows are not per mouth: they
 * are one wayfinding pass over the whole floor,
 * placed at a fixed density and every one of them aimed down a single distance
 * field to whichever way down is nearest. See the note at the top of the file
 * for why — four arrows clustered around each mouth put signage on five per
 * cent of the floor, which is the same five per cent the mouth was already
 * visible from, so they told nobody anything they did not already know.
 *
 * Runs after `placeProps`, which is also what makes it safe: that pass reserves
 * a two-cell ring around every exit against solid scenery, so the ground in
 * front of each mouth is already clear by the time this is called.
 */
export function installSlides(ctx: DressContext): void {
  const { grid, rng } = ctx;
  const t = descentPressure(ctx.spec);
  const mask = landmarkMask(grid, ctx.props);
  // Shared across the whole pass: nothing hangs two marks on one stretch of
  // wall, whichever mouth put them there.
  const taken: number[] = [];

  const mouths: Cell2[] = [];
  for (const plan of ctx.slides) {
    emitSlide(ctx, plan);
    mouths.push({ cx: plan.cell % grid.w, cy: (plan.cell / grid.w) | 0 });
  }
  if (mouths.length === 0) return;

  // One mark at each mouth, from that mouth's own field: a level-wide field
  // would let one plate stand in for two slides and leave the other unmarked.
  //
  // Which mark it is, is the depth ramp's second half. Near the top of the
  // building it is the building's EXIT plate; deeper it is more often what a
  // person wrote on the wall on their way in, which points at nothing and is
  // read from a metre away rather than from across the room. The last mouth is
  // forced back to a plate if the dice have left the level without one — the
  // point is a longer search, not a level where the only way down is unlabelled.
  const keepPlate = 1 - (1 - PLATE_KEPT) * t;
  let plates = 0;
  for (let i = 0; i < mouths.length; i++) {
    const toSlide = bfsDistances(grid, [mouths[i]!]);
    const cell = pickWall(ctx, mask, toSlide, 1, 5, taken, 0, () => 1);
    if (cell < 0) continue;
    // Short-circuited rather than written as one `chance`, so that at depth 0
    // this pass draws from the stream exactly as many times as it did before
    // any of this existed: Level 0 is meant to come out of a given seed
    // identical to the Level 0 the player already likes.
    const plate = keepPlate >= 1 || rng.chance(keepPlate) || (plates === 0 && i === mouths.length - 1);
    if (plate) plates++;
    emitWall(
      ctx,
      cell,
      wallOf(grid, cell),
      plate ? 'sign' : 'graffiti',
      plate ? SIGN_EXIT : rng.int(0, GRAFFITI_MOUTH_ROWS - 1)
    );
    stampLandmark(mask, grid, cell % grid.w, (cell / grid.w) | 0);
    taken.push(cell);
  }

  // The field the arrows descend is "how far to the nearest way down", not "how
  // far to the nearest slide". A building signposts its whole egress system,
  // not its favourite door — and Pipe Dreams is the level that proves it: a
  // one-cell maze where slides alone left twenty-three simulated players in
  // thirty unable to reach *any* exit inside a quarter of an hour, because the
  // field was so deep that no wall in a district could win five cells against
  // it. The ordinary exits already carry EXIT plates of their own
  // (`signpostExits`), so an arrow that leads to a stairwell is not a lie; the
  // slide is still the one that announces itself from across the room.
  const targets = mouths.slice();
  for (const exit of ctx.exits) {
    if (exit.kind === 'slide') continue;
    const c = grid.worldToCell(exit.pos.x, exit.pos.z);
    if (grid.isWalkable(c.cx, c.cy)) targets.push(c);
  }
  signTheFloor(ctx, mask, bfsDistances(grid, targets), taken, t);
}

/**
 * The wayfinding pass. Marks spread over every part of the floor a person walks
 * through, each pointing at the nearest way down.
 *
 * The level is cut into districts `signSpacing` metres across and each one gets
 * the single best wall in it — so the coverage is uniform by construction and
 * the count falls out of the level's size, the way the number of mouths does.
 * A budget with a spacing rule instead of this put every mark on the handful of
 * long halls that score best and left whole quarters of the map unsigned, which
 * is the failure this pass exists to fix.
 *
 * Two things a mark has to earn: somewhere to be read from (a run of clear
 * floor in front of it) and something true to say (a direction along its own
 * wall that actually gets closer). A district with nothing that qualifies gets
 * nothing — a quota that has to be filled is how a mark ends up on the inside
 * of a cupboard pointing at a wall.
 */
function signTheFloor(
  ctx: DressContext,
  mask: Uint8Array,
  toAny: Uint16Array,
  taken: number[],
  t: number
): void {
  const { grid, rng } = ctx;
  // Wider districts with depth, so there are fewer marks and further between
  // them: the count falls with the square of this. What it must not do is stop
  // being uniform — a level with signage in half of it and none in the other
  // half is not harder, it is two different levels.
  const block = Math.max(3, Math.round((SIGN_SPACING * (1 + SIGN_SPREAD * t)) / grid.cellSize));
  // And a higher bar for what an arrow has to promise, so the ones that survive
  // are the walls that genuinely line up with the way down.
  const minGain = ARROW_MIN_GAIN + ARROW_GAIN_RISE * t;
  const bw = Math.ceil(grid.w / block);
  const bh = Math.ceil(grid.h / block);
  const best = new Int32Array(bw * bh).fill(-1);
  const bestScore = new Float32Array(bw * bh);
  const bestSide = new Uint8Array(bw * bh);

  for (let y = 1; y < grid.h - 1; y++) {
    for (let x = 1; x < grid.w - 1; x++) {
      const i = y * grid.w + x;
      const d = toAny[i]!;
      if (d === UNREACHABLE || d < ARROW_MOUTH_CLEAR) continue;
      if (mask[i]) continue;
      const c = grid.cells[i]! as Cell;
      // Nothing is painted on the inside of a doorway, and a pillar cell has a
      // column through the middle of it.
      if (c !== Cell.Floor && c !== Cell.Damp && c !== Cell.Open && c !== Cell.Water) continue;
      const dir = wallOf(grid, i);
      if (dir < 0) continue;

      // Something true to say. This is the term that decides most districts:
      // three walls in four do not line up with the way down at all.
      const aim = arrowAim(grid, toAny, i, dir);
      if (aim.run < 1 || aim.gain < Math.min(minGain, aim.run)) continue;

      // How much room there is to read it from. Both ways count, and on the
      // levels made of corridors only the second one ever does: a mark on the
      // side wall of a passage is read by somebody walking *along* the passage,
      // never by somebody standing back from it. Measuring only the room in
      // front put no arrow anywhere in Pipe Dreams outside a junction.
      let head = 0;
      while (head < ARROW_SIGHT_CAP && grid.isWalkable(x - DX[dir] * (head + 1), y - DY[dir] * (head + 1))) {
        head++;
      }
      const sight = Math.max(head, Math.min(aim.run, ARROW_SIGHT_CAP));

      // A junction is where the way on is a question, which is where an answer
      // is worth having.
      let ways = 0;
      for (let k = 0; k < 4; k++) if (grid.isWalkable(x + DX[k], y + DY[k])) ways++;

      // The random term breaks ties, so two seeds of one level do not hang
      // their signage on the same walls.
      const s = aim.gain * 1.5 + sight + (sight >= ARROW_GOOD_SIGHT ? 3 : 0) + (ways >= 3 ? 4 : 0) + rng.next();
      const b = ((y / block) | 0) * bw + ((x / block) | 0);
      if (s <= bestScore[b]!) continue;
      bestScore[b] = s;
      best[b] = i;
      bestSide[b] = aim.side;
    }
  }

  for (let b = 0; b < best.length; b++) {
    const cell = best[b]!;
    if (cell < 0) continue;
    // The mask has been added to since this district was scored: a neighbouring
    // district's mark may have landed within the landmark clearance.
    if (mask[cell]) continue;
    const dir = wallOf(grid, cell);
    if (dir < 0) continue;
    emitWall(ctx, cell, dir, 'arrow', arrowVariant(bestSide[b]!, rng));
    stampLandmark(mask, grid, cell % grid.w, (cell / grid.w) | 0);
    taken.push(cell);
  }
}

/**
 * The same courtesy, smaller, for the ordinary ways down: one EXIT plate within
 * sight of each, unless the landmark pass already hung one there.
 *
 * `props.ts` reserves its first signs for the exits, but the sign budget is one
 * to three on a level with three of them, so on most seeds two exits get
 * nothing. This is the floor under that, not a replacement for it.
 */
export function signpostExits(ctx: DressContext): void {
  const { grid } = ctx;
  const mask = landmarkMask(grid, ctx.props);

  for (const exit of ctx.exits) {
    // A slide already has its own plate, hung by `installSlides` against the
    // wall it was fitted to. A second one four cells away is not more signal.
    if (exit.kind === 'slide') continue;
    const c = grid.worldToCell(exit.pos.x, exit.pos.z);
    if (!grid.isWalkable(c.cx, c.cy)) continue;
    const toExit = bfsDistances(grid, [c]);
    const cell = pickWall(ctx, mask, toExit, 1, 4, [], 0, () => 1);
    if (cell < 0) continue;
    emitWall(ctx, cell, wallOf(grid, cell), 'sign', SIGN_EXIT);
    stampLandmark(mask, grid, cell % grid.w, (cell / grid.w) | 0);
  }
}

/** The mouth: backed against its wall, facing the room, and solid. */
function emitSlide(ctx: DressContext, plan: SlidePlan): void {
  const { grid, rng, props } = ctx;
  const def = PROP_DEFS.slide;
  const fp = def.footprint!;
  const base = grid.cellToWorld(plan.cell % grid.w, (plan.cell / grid.w) | 0);
  // Set back by its own half-depth, exactly as `props.ts` does for a solid
  // floor prop, so the box lands where the object looks like it is.
  const back = Math.max(0, grid.cellSize * 0.5 - (fp.halfZ + SOLID_BACK));
  const dx = DX[plan.dir];
  const dz = DY[plan.dir];
  props.push({
    id: 0,
    pos: { x: base.x + dx * back, z: base.z + dz * back },
    // Facing out of the wall it comes through. yaw = 0 looks down -Z, so a
    // heading (vx, vz) is atan2(vx, -vz), and out of the wall is -(dx, dz).
    yaw: Math.atan2(-dx, dz),
    kind: 'slide',
    height: 0,
    scale: rng.range(def.scale[0], def.scale[1]),
    variant: rng.int(0, 0xffff),
    footprint: fp,
  });
}

/**
 * The best wall cell in a band of the distance field that nothing is using.
 *
 * `field` is a BFS from whatever is being pointed at, so a band is a ring of
 * approach at a known number of cells out.
 */
function pickWall(
  ctx: DressContext,
  mask: Uint8Array,
  field: Uint16Array,
  near: number,
  far: number,
  taken: readonly number[],
  separation: number,
  bonus: (cell: number) => number,
): number {
  const { grid } = ctx;
  let best = -1;
  let bestScore = 0;
  for (let y = 1; y < grid.h - 1; y++) {
    for (let x = 1; x < grid.w - 1; x++) {
      const i = y * grid.w + x;
      const d = field[i]!;
      if (d === UNREACHABLE || d < near || d > far) continue;
      if (mask[i]) continue;
      const c = grid.cells[i]! as Cell;
      // Nothing is painted on the inside of a doorway, and a pillar cell has a
      // column through the middle of it.
      if (c !== Cell.Floor && c !== Cell.Damp && c !== Cell.Open && c !== Cell.Water) continue;
      if (wallOf(grid, i) < 0) continue;

      let clear = true;
      for (const t of taken) {
        const tx = t % grid.w;
        const ty = (t / grid.w) | 0;
        if (Math.abs(tx - x) + Math.abs(ty - y) < separation) {
          clear = false;
          break;
        }
      }
      if (!clear) continue;

      let open = 0;
      for (let k = 0; k < 4; k++) if (grid.isWalkable(x + DX[k], y + DY[k])) open++;
      const s = open * bonus(i) + (far - d) * 0.05;
      if (s > bestScore) {
        bestScore = s;
        best = i;
      }
    }
  }
  return best;
}

/** Emit one wall-mounted prop, with the same offsets and facing as `props.ts`. */
function emitWall(ctx: DressContext, cell: number, dir: number, kind: PropKind, variant: number): void {
  const { grid, rng, spec, props } = ctx;
  if (dir < 0) return;
  const def = PROP_DEFS[kind];
  const base = grid.cellToWorld(cell % grid.w, (cell / grid.w) | 0);
  const back = Math.max(0, grid.cellSize * 0.5 - WALL_BACK);
  const dx = DX[dir];
  const dz = DY[dir];
  props.push({
    id: 0,
    pos: { x: base.x + dx * back, z: base.z + dz * back },
    yaw: Math.atan2(-dx, dz),
    kind,
    height: clamp(rng.range(def.height[0], def.height[1]), 0, spec.ceilingHeight - HEAD_ROOM),
    scale: rng.range(def.scale[0], def.scale[1]),
    variant,
  });
}

/**
 * Which way an arrow on this wall should point, and how much ground following
 * it wins.
 *
 * `side` is 0 for the prop's left and 1 for its right — the only two directions
 * that can be drawn on a wall the player is facing. `gain` is the most cells of
 * the distance field the better of the two gets rid of inside
 * `ARROW_LOOKAHEAD`, and a gain of zero means neither way is progress, which is
 * a real outcome on a wall in the middle of a room. Every arrow in the game is
 * a promise that walking that way is progress; one that is merely the better of
 * two wrong answers breaks the promise for all of them, so the caller declines
 * to paint it.
 */
function arrowAim(
  grid: Grid,
  field: Uint16Array,
  cell: number,
  dir: number
): { side: number; gain: number; run: number } {
  const along = dir < 2 ? [2, 3] : [0, 1];
  const here = field[cell]!;
  let best = -1;
  let bestGain = 0;
  let bestRun = 0;
  for (const a of along) {
    let x = cell % grid.w;
    let y = (cell / grid.w) | 0;
    let run = 0;
    // Measured where the walk *ends*, not at the best point along it. An arrow
    // is read as "keep going this way", and a direction that gets closer for
    // six metres and then dead-ends is a pocket the player has to be led back
    // out of by the same signage that put them in it.
    let gain = 0;
    for (let step = 0; step < ARROW_LOOKAHEAD; step++) {
      x += DX[a]!;
      y += DY[a]!;
      if (!grid.isWalkable(x, y)) break;
      const d = field[y * grid.w + x]!;
      if (d === UNREACHABLE) break;
      gain = here - d;
      run++;
    }
    if (gain > bestGain) {
      bestGain = gain;
      bestRun = run;
      best = a;
    }
  }
  if (best < 0) return { side: 0, gain: 0, run: 0 };
  // Facing out of the wall, "right" is 90 degrees clockwise from that facing:
  // for a facing of (-dx, -dz) that is (dz, -dx).
  const rightX = DY[dir];
  const rightZ = -DX[dir];
  return { side: DX[best]! * rightX + DY[best]! * rightZ > 0 ? 1 : 0, gain: bestGain, run: bestRun };
}

/**
 * The side from `arrowAim`, packed into a `PropSpot.variant`: the low bit is
 * the side and the rest is the usual per-prop variation seed. See
 * `PropSpot.variant`, and `render/propdecals.ts` for why the bit has to stay
 * the low one.
 */
function arrowVariant(side: number, rng: RNG): number {
  return (rng.int(0, 0x3fff) << 1) | side;
}
